import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableHead,
  TableRow,
  Button,
  Paper
} from '@mui/material';
import {
  getPatientHistory,
  getPatientFromVquraById,
  getPatientById
} from '../api/patientApi';
import { getAllPatientReports } from '../api/answersApi';
import BarChartLevel from '../components/BarChartLevel';

const PatientHistory = () => {
  const { patientId } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientLoading, setPatientLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [patientDetails, setPatientDetails] = useState(null);
  const [reports, setReports] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        let patientData = null;

        // 1. Try VQURA API first
        try {
          const vquraData = await getPatientFromVquraById(patientId);
          if (vquraData && vquraData.user_id) {
            patientData = {
              id: vquraData.user_id,
              name: `${vquraData.first_name} ${vquraData.last_name || ''}`.trim(),
              mobileNo: vquraData.mobile_number,
              dateOfBirth: vquraData.dob,
              gender: vquraData.gender,
              city: vquraData.city,
              state: vquraData.state,
              source: 'vqura',
            };
          }
        } catch (err) {
          console.warn('VQURA fetch failed:', err);
        }

        // 2. If no valid VQURA data, try local API
        if (!patientData) {
          try {
            const res = await getPatientById(patientId);
            if (res.data?.succeeded && res.data.data) {
              const p = res.data.data;
              patientData = {
                id: p.id,
                name: p.name,
                mobileNo: p.mobileNo,
                dateOfBirth: p.dateOfBirth,
                gender: p.gender || '-',
                city: p.city || '-',
                state: p.state || '-',
                source: 'local',
              };
            }
          } catch (err) {
            console.warn('Local fetch failed:', err);
          }
        }

        setPatientDetails(patientData);
      } catch (err) {
        console.error('Error fetching Patient:', err);
      } finally {
        setPatientLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getPatientHistory(patientId);
        if (res.data.succeeded) {
          setHistory(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [patientId]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await getAllPatientReports(patientId);
        if (res.data.succeeded) {
          setReports(res.data.data);
        }
      } catch (error) {
        console.error('Failed to load reports:', error);
      } finally {
        setReportLoading(false);
      }
    };

    if (patientId) fetchReports();
  }, [patientId]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewReport = (clickedReport) => {
    const sortedReports = [...reports].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    const currentIndex = sortedReports.findIndex(
      (r) => r.createdAt === clickedReport.createdAt
    );

    const reportDataArray = sortedReports.slice(currentIndex);

    navigate(`/patient/${patientId}/report`, {
      state: {
        patientId,
        patientName: patientDetails ? patientDetails.name : '-',
        staffName: clickedReport.staffName,
        reportDataArray,
      },
    });
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box textAlign="right" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/patient`, { replace: true })}
        >
          Home
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom>
        Patient History
      </Typography>

      {patientLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : !patientDetails ? (
        <Typography>Invalid Patient</Typography>
      ) : (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid #ea258b',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            backgroundColor: '#fdf6fa',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography><strong>Name:</strong> {patientDetails.name}</Typography>
            <Typography><strong>Mobile:</strong> {patientDetails.mobileNo}</Typography>
            {patientDetails.dateOfBirth && (
              <>
                <Typography>
                  <strong>DOB:</strong> {new Date(patientDetails.dateOfBirth).toLocaleDateString()}
                </Typography>
                <Typography>
                  <strong>Age:</strong> {calculateAge(patientDetails.dateOfBirth)} years
                </Typography>
              </>
            )}
            <Typography><strong>City:</strong> {patientDetails.city}</Typography>
            <Typography><strong>State:</strong> {patientDetails.state}</Typography>
            <Typography sx={{ color: '#ea258b' }}>
              <em>Source: {patientDetails.source}</em>
            </Typography>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Typography>No assessments found on selected date.</Typography>
      ) : (
        <BarChartLevel data={history} />
      )}

      {reportLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Typography>No assessments found on selected date.</Typography>
      ) : (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#ea258b' }}>
            All Reports
          </Typography>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        onClick={() => handleViewReport(report)}
                      >
                        View Report
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
};

export default PatientHistory;
